'use client'

/*
 * SiteBackground — the config-driven background system (Phase 4).
 *
 * Replaces CloudLayer + the body sky gradient + the homepage RainEffect. Reads
 * `site.background` and renders exactly one effect layer (lava / aurora / grid /
 * dots / topo / plain) plus optional live grain. Every brand-specific number
 * lives in `src/site/sites/<id>/background.ts`; this file holds none.
 *
 * Gating (SOURCE §1.4, PHASE-04 §2.2–2.3):
 *  - hydration guard, same shape as RainEffect (client-only, avoids SSR/canvas)
 *  - prefers-reduced-motion is a hard stop; subscribes to live changes
 *  - desktop respects useAnimation() (the app's animation kill-switch)
 *  - mobile takes the lighter always-on path (D-7, owner-approved 2026-09-17):
 *    the <768px default-off is bypassed for the background and a smaller render
 *    scale is used instead. Reduced-motion still stops it.
 */

import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'

import { site } from '@/site'
import { useAnimation } from '@/providers/Animation'
import { useTheme } from '@/providers/Theme'

import { Grain } from './Grain'
import { EffectLayer } from './EffectLayer'
import styles from './background.module.css'

// Dynamic + ssr:false so the shader chunk is never in the server bundle and,
// crucially, is only fetched when effect === 'lava'. effect: 'plain' ships no
// shader code at all.
const LavaLayer = dynamic(() => import('./LavaLayer').then((m) => m.LavaLayer), {
  ssr: false,
  loading: () => null,
})

const MOBILE_MAX = 767 // < 768px is the app's mobile threshold

function scaleForQuality(quality: BackgroundQuality, isMobile: boolean): number {
  // The mobile lighter path (D-7) always wins over the quality knob.
  if (isMobile) return 0.3
  switch (quality) {
    case 'low':
      return 0.3
    case 'medium':
      return 0.42
    case 'high':
      return 0.6
    case 'auto':
    default:
      return 0.42 // prototype SCALE
  }
}

type BackgroundQuality = (typeof site)['background']['quality']

/**
 * The lava shader's `lo` uniform is the void colour behind the wax, and empty
 * regions of the canvas render fully transparent — so the layer needs an opaque
 * base of that same colour behind it, or in light mode the pale wax sits on the
 * page's white surface and vanishes. This base is the prototype's `--page`
 * (which equals `lo`), kept config-driven rather than hardcoded.
 */
function rgbCss([r, g, b]: readonly [number, number, number]): string {
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255)
  return `rgb(${to255(r)}, ${to255(g)}, ${to255(b)})`
}

export function SiteBackground() {
  const bg = site.background
  const { animationsEnabled } = useAnimation()
  const { theme } = useTheme()

  const [isClient, setIsClient] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    setIsClient(true)

    const rm = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(rm.matches)
    const rmHandler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    rm.addEventListener('change', rmHandler)

    const mob = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`)
    setIsMobile(mob.matches)
    const mobHandler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mob.addEventListener('change', mobHandler)

    return () => {
      rm.removeEventListener('change', rmHandler)
      mob.removeEventListener('change', mobHandler)
    }
  }, [])

  // Hydration guard — no canvas or random noise until after mount.
  if (!isClient) return null

  // Reduced motion → nothing animated renders (page falls back to the solid
  // theme background). Desktop honours the kill-switch; mobile stays on (light).
  const active = prefersReducedMotion ? false : isMobile ? true : animationsEnabled
  if (!active) return null

  const resolvedTheme: 'light' | 'dark' = theme === 'light' ? 'light' : 'dark'
  const scale = scaleForQuality(bg.quality, isMobile)

  // Opaque base behind the shader so the (semi-transparent) lava reads on any
  // page surface. Only for lava; other effects let the page background show.
  const rootStyle =
    bg.effect === 'lava' && bg.lava
      ? { backgroundColor: rgbCss(bg.lava.palette[resolvedTheme].lo) }
      : undefined

  return (
    <div className={styles.root} style={rootStyle} aria-hidden="true">
      {bg.effect === 'lava' && bg.lava && (
        <LavaLayer lava={bg.lava} theme={resolvedTheme} scale={scale} pauseOnScroll={bg.pauseOnScroll} />
      )}

      {bg.effect !== 'lava' && bg.effect !== 'plain' && (
        <EffectLayer effect={bg.effect} config={bg} />
      )}

      {bg.grain.enabled && (
        <Grain
          opacity={bg.grain.opacity}
          frames={bg.grain.tileCount}
          fps={bg.grain.fps}
          blend={resolvedTheme === 'light' ? 'multiply' : 'overlay'}
          reducedMotion={prefersReducedMotion}
          mobile={isMobile}
        />
      )}
    </div>
  )
}

export default SiteBackground
